import * as Yup from "yup";
import {tempEmailDomains} from "./TempEmail";

export const RegisterSchema = (emailBroker = null) =>
  Yup.object().shape({
    firstName: Yup.string()
      .matches(
        /^[A-Za-z]+(?:\s[A-Za-z]+)*$/,
        "First name can only contain alphabet with spaces between words",
      )
      .required("First name is required"),

    lastName: Yup.string()
      .matches(
        /^[A-Za-z]+(?:\s[A-Za-z]+)*$/,
        "Last name can only contain alphabet with spaces between words",
      )
      .required("Last name is required"),

    email: Yup.string()
      .email("Enter your email address in format: yourname@example.com")
      .max(256, "Email can have a maximum of 256 characters")
      .required("Email is required")
      .test(
        "is-not-temp-email",
        "Temporary email addresses are not allowed",
        (value) => {
          if (!value) return false;

          if (process.env.REACT_APP_ENV !== "production") return true;

          const domain = value.includes("@")
            ? value.split("@")[1].toLowerCase()
            : null;
          if (!domain) return true;
          return !tempEmailDomains.some(
            (tempDomain) =>
              domain === tempDomain || domain.endsWith(`.${tempDomain}`),
          );
        },
      )
      .test(
        "is-not-broker-email",
        "Customer email can't be the same as the broker email",
        (value) => {
          if (!emailBroker || !value) return true;
          return value.toLowerCase() !== emailBroker.toLowerCase();
        },
      ),

    phoneNumber: Yup.string()
      .required("Phone number is required")
      .matches(
        /^\+61 4\d{8}$/,
        "Enter a valid Australian mobile number (8 digits after +614)",
      ),
  });

export const LoginSchema = Yup.object().shape({
  email: Yup.string()
    .email("Enter your email address in format: yourname@example.com")
    .max(256, "Email can have a maximum of 256 characters")
    .required("Email is required")
    .test(
      "is-not-temp-email",
      "Temporary email addresses are not allowed",
      (value) => {
        if (!value) return false;

        if (process.env.REACT_APP_ENV !== "production") return true;

        const domain = value.includes("@")
          ? value.split("@")[1].toLowerCase()
          : null;
        if (!domain) return true;

        return !tempEmailDomains.some(
          (tempDomain) =>
            domain === tempDomain || domain.endsWith(`.${tempDomain}`),
        );
      },
    ),
});

export const LoanAmountSchema = Yup.object().shape({
  loanAmount: Yup.number()
    .typeError("Please enter amount between $300,000 to $10,000,000")
    .required("Loan amount is required")
    .min(300000, "Please enter amount between $300,000 to $10,000,000")
    .max(10000000, "Please enter amount between $300,000 to $10,000,000")
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        const numericValue = parseFloat(originalValue.replace(/,/g, ""));
        return isNaN(numericValue) ? undefined : numericValue;
      }
      return value;
    }),
});

export const LoanTermSchema = Yup.object().shape({
  loanTerm: Yup.number()
    .typeError("Please enter loan term between 1-24 months")
    .required("Loan term (1-24 months) is required")
    .min(1, "Please enter loan term between 1-24 months")
    .max(24, "Please enter loan term between 1-24 months"),
});

export const FundingTimeSchema = Yup.object().shape({
  expectedTime: Yup.string().required("Funding time is required"),
});

export const PreferredRepaymentSchema = Yup.object().shape({
  repaymentMethod: Yup.string().required("Preferred repayment is required"),
});

export const InterestOnlySchema = Yup.object().shape({
  averageMonthlyTurnOver: Yup.string()
    .required("Average monthly turnover is required")
    .test(
      "is-valid-number",
      "Please enter an amount greater than or equal to $1",
      function (value) {
        if (!value) return false;
        if (value.endsWith(".")) {
          return false;
        }
        const numericValue = parseFloat(value.replace(/,/g, ""));
        if (isNaN(numericValue)) {
          return false;
        }
        return numericValue >= 1;
      },
    ),
  totalBusinessAssets: Yup.string()
    .required("Total business assets is required")
    .test(
      "is-valid-number",
      "Please enter an amount greater than or equal to $1",
      function (value) {
        if (!value) return false;
        if (value.endsWith(".")) {
          return false;
        }
        const numericValue = parseFloat(value.replace(/,/g, ""));
        if (isNaN(numericValue)) {
          return false;
        }
        return numericValue >= 1;
      },
    ),
  totalBusinessLiabilities: Yup.string()
    .required("Total business liabilities is required")
    .test(
      "is-valid-number",
      "Please enter an amount greater than or equal to $1",
      function (value) {
        if (!value) return false;
        if (value.endsWith(".")) {
          return false;
        }
        const numericValue = parseFloat(value.replace(/,/g, ""));
        if (isNaN(numericValue)) {
          return false;
        }
        return numericValue >= 1;
      },
    ),
});

export const LoanPurposeSchema = Yup.object().shape({
  loanPurpose: Yup.string().required("Loan purpose is required"),
});

export const LoanPaybackSchema = Yup.object().shape({
  loanPayBackOption: Yup.string().required("Loan payback is required"),
});

export const BrokerCommissionRateSchema = Yup.object().shape({
  brokerCommissionRate: Yup.string().required(
    "Broker commission rate is required",
  ),
});

export const abnSchema = Yup.object().shape({
  abn: Yup.string()
    .matches(/^\d{11}$/, "Enter 11-digit ABN")
    .required("ABN is required"),
});

export const EntityTypeSchema = Yup.object().shape({
  entityType: Yup.string().required("Entity type is required"),
});

export const LegalEntityNameSchema = Yup.object().shape({
  legalEntityType: Yup.string()
    .matches(
      /^[A-Za-z0-9\s.,'-]*$/,
      "Only characters, and basic punctuation (.,'-) are allowed.",
    )
    .test("min-alphanum", "Must contain at least 2 characters.", (value) => {
      if (!value) return false;
      const alphanumCount = (value.match(/[A-Za-z0-9]/g) || []).length;
      return alphanumCount >= 2;
    })
    .required("Legal entity is required"),
});

export const IsAnyExistingLoanSchema = Yup.object().shape({
  isAnyExistingLoan: Yup.boolean()
    .required("Any existing loan is required")
    .oneOf([true, false], "Property Owned is required"),
});

export const IsPropertyOwnedSchema = Yup.object().shape({
  isPropertyOwned: Yup.boolean()
    .required("Property Owned is required")
    .oneOf([true, false], "Property Owned is required"),
});

export const NoOfPropertiesSchema = Yup.object().shape({
  noOfProperties: Yup.string()
    .required("Number of properties is required")
    .matches(/^[0-9]+$/, "Enter whole numbers only.")
    .test("min", "Minimum allowed is 1", (value) => Number(value) >= 1)
    .test("max", "Maximum allowed is 10", (value) => Number(value) <= 10),
});

export const PropertyTypeSchema = Yup.object().shape({
  propertyType: Yup.string().required("Property type is required"),
});

export const PropertyAddressSchema = Yup.object().shape({
  propertyAddress: Yup.string().required("Property address is required"),
});

export const MultiPropertyAddressSchema = Yup.object().shape({
  propertyAddress: Yup.string().required("Property address is required"),
  propertyType: Yup.string().required("Property type is required"),
  estimatedPropertyAmount: Yup.string()
    .required("Estimated property amount is required")
    .test(
      "is-valid-number",
      "Please enter an amount of at least $1",
      function (value) {
        if (!value) return false;
        if (value.endsWith(".")) {
          return false;
        }
        const numericValue = parseFloat(value.replace(/,/g, ""));
        if (isNaN(numericValue)) {
          return false;
        }
        return numericValue >= 1;
      },
    ),
});

export const EstimatedPropertyAmountSchema = Yup.object().shape({
  estimatedPropertyAmount: Yup.string()
    .required("Estimated property amount is required")
    .test(
      "is-valid-number",
      "Please enter an amount of at least $1",
      function (value) {
        if (!value) return false;
        if (value.endsWith(".")) {
          return false;
        }
        const numericValue = parseFloat(value.replace(/,/g, ""));
        if (isNaN(numericValue)) {
          return false;
        }
        return numericValue >= 1;
      },
    ),
});

export const MortgageSchema = Yup.object().shape({
  existingLoanSettlementOption: Yup.string().required(
    "First mortgage on the property/security this loan is required",
  ),
});

export const ExistingLoanAmountSchema = (estimatedPropertyAmountData) =>
  Yup.object().shape({
    isAnyExistingLoan: Yup.boolean()
      .required("Any existing loan is required")
      .oneOf([true, false], "Property Owned is required"),
    existingLoanAmount: Yup.number().when("isAnyExistingLoan", {
      is: true,
      then: (schema) =>
        schema
          .typeError("Please enter amount between $1 to $10,000,000")
          .required("Estimated property amount is required")
          .min(
            1,
            `Please enter amount between $1 to $${estimatedPropertyAmountData}`,
          )
          .test(
            "max-less-than-property",
            "Existing loan amount cannot exceed or equal estimated property amount",
            function (value) {
              if (!estimatedPropertyAmountData) return true;

              let propertyValue;
              try {
                if (typeof estimatedPropertyAmountData === "string") {
                  const cleaned = estimatedPropertyAmountData
                    .replace(/,/g, "")
                    .trim();
                  propertyValue = parseFloat(cleaned);
                  if (isNaN(propertyValue))
                    throw new Error("Invalid property value");
                } else if (typeof estimatedPropertyAmountData === "number") {
                  propertyValue = estimatedPropertyAmountData;
                } else {
                  throw new Error("Unexpected data type");
                }
              } catch (err) {
                console.error(
                  "Invalid estimatedPropertyAmountData:",
                  err.message,
                );
                return true;
              }

              if (!value) return true;

              if (value === propertyValue) {
                return this.createError({
                  message: "Loan amount should be less than the property value",
                });
              }

              return value < propertyValue;
            },
          )
          .transform((value, originalValue) => {
            if (typeof originalValue === "string") {
              const numericValue = parseFloat(originalValue.replace(/,/g, ""));
              return isNaN(numericValue) ? undefined : numericValue;
            }
            return value;
          }),
      otherwise: (schema) => schema.notRequired().strip(),
    }),
  });

export const IsAnyCaveatsSchema = Yup.object().shape({
  isAnyCaveats: Yup.boolean()
    .required("Caveats is required")
    .oneOf([true, false], "Caveats is required"),
});

export const PaymentFormSchema = Yup.object().shape({
  email: Yup.string()
    .email("Enter your email address in format: yourname@example.com")
    .max(256, "Email can have a maximum of 256 characters")
    .required("Email is required")
    .test(
      "is-not-temp-email",
      "Temporary email addresses are not allowed",
      (value) => {
        if (!value) return false;

        if (process.env.REACT_APP_ENV !== "production") return true;

        const domain = value.includes("@")
          ? value.split("@")[1].toLowerCase()
          : null;
        if (!domain) return true;

        return !tempEmailDomains.some(
          (tempDomain) =>
            domain === tempDomain || domain.endsWith(`.${tempDomain}`),
        );
      },
    ),
  cardName: Yup.string().required("Cardholder name is required"),
  cardCountry: Yup.string().required("Country is required"),
  cardAddress: Yup.string().required("Address is required"),
});

export const DirectorsSchema = Yup.object().shape({
  memberRole: Yup.mixed().required("Role is required"),
  memberFirstName: Yup.string()
    .matches(
      /^[A-Za-z]+(?:\s[A-Za-z]+)*$/,
      "Only alphabet with spaces between words",
    )
    .required("First name is required"),
  memberLastName: Yup.string()
    .matches(
      /^[A-Za-z]+(?:\s[A-Za-z]+)*$/,
      "Only alphabet with spaces between words",
    )
    .required("Last name is required"),
  memberEmail: Yup.string()
    .email("Enter your email address in format: yourname@example.com")
    .max(256, "Email can have a maximum of 256 characters")
    .required("Email is required")
    .test(
      "is-not-temp-email",
      "Temporary email addresses are not allowed",
      (value) => {
        if (!value) return false;

        if (process.env.REACT_APP_ENV !== "production") return true;

        const domain = value.includes("@")
          ? value.split("@")[1].toLowerCase()
          : null;
        if (!domain) return true;

        return !tempEmailDomains.some(
          (tempDomain) =>
            domain === tempDomain || domain.endsWith(`.${tempDomain}`),
        );
      },
    ),
  memberPhoneNumber: Yup.string()
    .required("Phone number is required")
    .matches(
      /^\+61 4\d{8}$/,
      "Enter a valid Australian mobile number (8 digits after +614)",
    ),
  memberResidentialAddress: Yup.string().required(
    "Residential address is required",
  ),
});

export const SolicitorSchema = Yup.object().shape({
  solicitorfirstName: Yup.string()
    .matches(
      /^[A-Za-z]+(?:\s[A-Za-z]+)*$/,
      "First name can only contain alphabet with spaces between words",
    )
    .required("First name is required"),

  solicitorLastName: Yup.string()
    .matches(
      /^[A-Za-z]+(?:\s[A-Za-z]+)*$/,
      "Last name can only contain alphabet with spaces between words",
    )
    .required("Last name is required"),

  solicitorBusinessName: Yup.string().required("Business name is required"),
  solicitorWorkEmailAddress: Yup.string()
    .email("Enter email address in format: yourname@example.com")
    .max(256, "Email can have a maximum of 256 characters")
    .required("Work email address is required")
    .test("is-valid-email", "Invalid Email", (value) => {
      if (!value) return false;
      const domain = value.split("@")[1];

      return true;
    }),
});

export const BankSchema = Yup.object().shape({
  BSB: Yup.string()
    .matches(/^\d{6}$/, "BSB must have 6 digits")
    .required("BSB is required"),

  accountNumber: Yup.string()
    .matches(
      /^\d{6,12}$/,
      "Account number must be 6–10 digits, or up to 12 in some cases",
    )
    .required("Account number is required"),

  accountName: Yup.string()
    .matches(
      /^[A-Za-z]+(?:\s[A-Za-z]+)*$/,
      "First name can only contain alphabet with spaces between words",
    )
    .required("Account name is required"),
});

export const YourLoanReviewSchema = Yup.object().shape({
  loanAmount: Yup.number()
    .typeError("Please enter amount between $300,000 to $10,000,000")
    .required("Loan amount is required")
    .min(300000, "Please enter amount between $300,000 to $10,000,000")
    .max(10000000, "Please enter amount between $300,000 to $10,000,000")
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        const numericValue = parseFloat(originalValue.replace(/,/g, ""));
        return isNaN(numericValue) ? undefined : numericValue;
      }
      return value;
    }),
  loanTerm: Yup.number()
    .typeError("Please enter loan term between 1-24 months")
    .required("Loan term (1-24 months) is required")
    .min(1, "Please enter loan term between 1-24 months")
    .max(24, "Please enter loan term between 1-24 months"),
  expectedTime: Yup.string().required("Funding time is required"),
  repaymentMethod: Yup.string().required("Preferred repayment is required"),
  // loanPurpose: Yup.string().when("repaymentMethod", {
  //   is: "Interest Capitalised",
  //   then: (schema) => schema.notRequired(),
  //   otherwise: (schema) => schema.required("Loan purpose is required"),
  // }),
});

export const YourBusinessReviewSchema = Yup.object().shape({
  entityType: Yup.string().required("Entity type is required"),
  legalEntityType: Yup.string()
    .matches(
      /^[A-Za-z0-9\s.,'-]*$/,
      "Only characters, and basic punctuation (.,'-) are allowed.",
    )
    .test("min-alphanum", "Must contain at least 2 characters.", (value) => {
      if (!value) return false;
      const alphanumCount = (value.match(/[A-Za-z0-9]/g) || []).length;
      return alphanumCount >= 2;
    })
    .required("Legal entity is required"),
  registeredEntityName: Yup.string().required("Registered entity is required"),
  abn: Yup.string()
    // .matches(/^\d{9}(\d{2})?$/, "11-digit ABN / 9-digit ACN")
    .matches(/^\d{11}$/, "Enter 11-digit ABN")
    .required("ABN is required"),
  entityAddress: Yup.string().required("Entity address is required"),
});

export const PropertyDetailsReviewSchema = (currentProfile, status) =>
  Yup.object().shape({
    noOfProperties: Yup.string()
      .required("Number of properties is required")
      .matches(/^[0-9]+$/, "Enter whole numbers only.")
      .test("min", "Minimum allowed is 1", (value) => Number(value) >= 1)
      .test("max", "Maximum allowed is 10", (value) => Number(value) <= 10),
    propertyType: Yup.string().required("Property type is required"),
    propertyAddress: Yup.string().required("Property address is required"),
    estimatedPropertyAmount:
      status !== "Add"
        ? null
        : Yup.string()
            .required("Estimated property amount is required")
            .min(
              1,
              `Please enter amount between $1 to $${currentProfile.existingLoanAmount}`,
            )
            .test(
              "less-than-existing-loan",
              "Estimated property amount must be greater than existing loan amount",
              function (value) {
                if (!currentProfile?.existingLoanAmount) return true;
                const existingLoan = parseFloat(
                  currentProfile.existingLoanAmount
                    .toString()
                    .replace(/,/g, ""),
                );
                const propertyValue = parseFloat(value.replace(/,/g, ""));
                if (isNaN(existingLoan) || isNaN(propertyValue)) return true;
                return propertyValue > existingLoan;
              },
            ),
    isAnyExistingLoan: Yup.boolean().required("Any existing loan is required"),
    existingLoanAmount:
      status === "Add"
        ? Yup.number().when("isAnyExistingLoan", {
            is: true,
            then: (schema) =>
              schema
                .typeError("Please enter amount between $1 to $10,000,000")
                .required("Estimated property amount is requiredaa")
                .min(
                  1,
                  `Please enter amount between $1 to $${currentProfile.estimatedPropertyAmount}`,
                )
                .test(
                  "max-less-than-property",
                  "Existing loan amount cannot exceed or equal estimated property amount",
                  function (value) {
                    const amount = currentProfile?.estimatedPropertyAmount;
                    if (!amount) return true;
                    const propertyValue =
                      typeof amount === "number"
                        ? amount
                        : parseFloat(amount.replace(/,/g, ""));
                    if (isNaN(propertyValue) || !value) return true;
                    if (value === propertyValue)
                      return this.createError({
                        message:
                          "Loan amount should be less than the property value",
                      });
                    return value < propertyValue;
                  },
                )
                .transform((value, originalValue) => {
                  if (typeof originalValue === "string") {
                    const numericValue = parseFloat(
                      originalValue.replace(/,/g, ""),
                    );
                    return isNaN(numericValue) ? undefined : numericValue;
                  }
                  return value;
                }),
            otherwise: (schema) => schema.notRequired().strip(),
          })
        : Yup.number().when("isAnyExistingLoan", {
            is: () => currentProfile?.isAnyExistingLoan === true,
            then: (schema) =>
              schema
                .typeError("Please enter amount between $1 to $10,000,000")
                .required("Estimated property amount is requiredbb")
                .min(
                  1,
                  `Please enter amount between $1 to $${currentProfile.estimatedPropertyAmount}`,
                )
                .test(
                  "max-less-than-property",
                  "Existing loan amount cannot exceed or equal estimated property amount",
                  function (value) {
                    const amount = currentProfile?.estimatedPropertyAmount;
                    if (!amount) return true;
                    const propertyValue =
                      typeof amount === "number"
                        ? amount
                        : parseFloat(amount.replace(/,/g, ""));
                    if (isNaN(propertyValue) || !value) return true;
                    if (value === propertyValue)
                      return this.createError({
                        message:
                          "Loan amount should be less than the property value",
                      });
                    return value < propertyValue;
                  },
                )
                .transform((value, originalValue) => {
                  if (typeof originalValue === "string") {
                    const numericValue = parseFloat(
                      originalValue.replace(/,/g, ""),
                    );
                    return isNaN(numericValue) ? undefined : numericValue;
                  }
                  return value;
                }),
            otherwise: (schema) => schema.notRequired().strip(),
          }),
    existingLoanSettlementOption:
      status === "Add"
        ? Yup.string().when("isAnyExistingLoan", {
            is: (val) => val === true,
            then: (schema) =>
              schema
                .required(
                  "First mortgage on the property/security this loan is required",
                )
                .test(
                  "not-undecided",
                  "Please choose an option other than 'I’ll decide later'",
                  (value) => value !== "I’ll decide later",
                ),
            otherwise: (schema) => schema.notRequired().strip(),
          })
        : Yup.string().when("isAnyExistingLoan", {
            is: () => currentProfile?.isAnyExistingLoan === true,
            then: (schema) =>
              schema.required(
                "First mortgage on the property/security this loan is required",
              ),
            otherwise: (schema) => schema.notRequired().strip(),
          }),
    isAnyCaveats: Yup.boolean()
      .required("Caveats is required")
      .oneOf([true, false], "Caveats is required"),
  });

export const ContactUsSchema = Yup.object().shape({
  firstName: Yup.string()
    .matches(
      /^[A-Za-z]+(?:\s[A-Za-z]+)*$/,
      "First name can only contain alphabet with spaces between words",
    )
    .required("First name is required"),

  lastName: Yup.string()
    .matches(
      /^[A-Za-z]+(?:\s[A-Za-z]+)*$/,
      "Last name can only contain alphabet with spaces between words",
    )
    .required("Last name is required"),

  email: Yup.string()
    .email("Enter your email address in format: yourname@example.com")
    .max(256, "Email can have a maximum of 256 characters")
    .required("Email is required")
    .test(
      "is-not-temp-email",
      "Temporary email addresses are not allowed",
      (value) => {
        if (!value) return false;

        if (process.env.REACT_APP_ENV !== "production") return true;

        const domain = value.includes("@")
          ? value.split("@")[1].toLowerCase()
          : null;
        if (!domain) return true;

        return !tempEmailDomains.some(
          (tempDomain) =>
            domain === tempDomain || domain.endsWith(`.${tempDomain}`),
        );
      },
    ),

  phoneNumber: Yup.string()
    .required("Phone number is required")
    .matches(
      /^4\d{8}$/,
      "Enter a valid Australian mobile number (8 digits after +614)",
    ),
  message: Yup.string().required("Message is required"),
});

export const EarlyAccessSchema = Yup.object().shape({
  firstName: Yup.string()
    .matches(
      /^[A-Za-z]+(?:\s[A-Za-z]+)*$/,
      "First name can only contain alphabet with spaces between words",
    )
    .required("First name is required"),

  lastName: Yup.string()
    .matches(
      /^[A-Za-z]+(?:\s[A-Za-z]+)*$/,
      "Last name can only contain alphabet with spaces between words",
    )
    .required("Last name is required"),

  email: Yup.string()
    .email("Enter your email address in format: yourname@example.com")
    .max(256, "Email can have a maximum of 256 characters")
    .required("Email is required")
    .test(
      "is-not-temp-email",
      "Temporary email addresses are not allowed",
      (value) => {
        if (!value) return false;

        if (process.env.REACT_APP_ENV !== "production") return true;

        const domain = value.includes("@")
          ? value.split("@")[1].toLowerCase()
          : null;
        if (!domain) return true;

        return !tempEmailDomains.some(
          (tempDomain) =>
            domain === tempDomain || domain.endsWith(`.${tempDomain}`),
        );
      },
    ),

  phoneNumber: Yup.string()
    .required("Phone number is required")
    .matches(
      /^4\d{8}$/,
      "Enter a valid Australian mobile number (8 digits after +614)",
    ),
  companyName: Yup.string().required("Company name is required"),
  jobTitle: Yup.string().required("Job title is required"),
});

export const NewPropertiesSchema = (estimatedPropertyAmountData) =>
  Yup.object().shape({
    estimatedPropertyAmount: Yup.string()
      .required("Estimated property amount is required")
      .test(
        "is-valid-number",
        "Please enter an amount of at least $1",
        function (value) {
          if (!value) return false;
          if (value.endsWith(".")) {
            return false;
          }
          const numericValue = parseFloat(value.replace(/,/g, ""));
          if (isNaN(numericValue)) {
            return false;
          }
          return numericValue >= 1;
        },
      ),
    existingLoanAmount: Yup.number().when("isAnyExistingLoan", {
      is: true,
      then: (schema) =>
        schema
          .typeError("Please enter amount between $1 to $10,000,000")
          .required("Estimated property amount is required")
          .min(
            1,
            `Please enter amount between $1 to $${estimatedPropertyAmountData}`,
          )
          .test(
            "max-less-than-property",
            "Existing loan amount cannot exceed or equal estimated property amount",
            function (value) {
              if (!estimatedPropertyAmountData) return true;

              let propertyValue;
              try {
                if (typeof estimatedPropertyAmountData === "string") {
                  const cleaned = estimatedPropertyAmountData
                    .replace(/,/g, "")
                    .trim();
                  propertyValue = parseFloat(cleaned);
                  if (isNaN(propertyValue))
                    throw new Error("Invalid property value");
                } else if (typeof estimatedPropertyAmountData === "number") {
                  propertyValue = estimatedPropertyAmountData;
                } else {
                  throw new Error("Unexpected data type");
                }
              } catch (err) {
                console.error(
                  "Invalid estimatedPropertyAmountData:",
                  err.message,
                );
                return true;
              }

              if (!value) return true;

              if (value === propertyValue) {
                return this.createError({
                  message: "Loan amount should be less than the property value",
                });
              }

              return value < propertyValue;
            },
          )
          .transform((value, originalValue) => {
            if (typeof originalValue === "string") {
              const numericValue = parseFloat(originalValue.replace(/,/g, ""));
              return isNaN(numericValue) ? undefined : numericValue;
            }
            return value;
          }),
      otherwise: (schema) => schema.notRequired().strip(),
    }),
    existingLoanSettlementOption: Yup.string().when("isAnyExistingLoan", {
      is: true,
      then: (schema) =>
        schema
          .required(
            "First mortgage on the property/security this loan is required",
          )
          .test(
            "not-undecided",
            "Please choose an option other than 'I’ll decide later'",
            (value) => value !== "I’ll decide later",
          ),
      otherwise: (schema) => schema.notRequired().strip(),
    }),
    isAnyCaveats: Yup.boolean()
      .required("Caveats is required")
      .oneOf([true, false], "Caveats is required"),
    isAnyExistingLoan: Yup.boolean()
      .required("Any existing loan is required")
      .oneOf([true, false], "Property Owned is required"),
    propertyAddress: Yup.string().required("Property address is required"),
    propertyType: Yup.string().required("Property type is required"),
  });

export const AnnualTurnoverSchema = Yup.object().shape({
  annualTurnover: Yup.number()
    .typeError("Please enter amount between $1 to $20,000,000")
    .required("Annual Turnover is required")
    .min(1, "Please enter amount between $1 to $20,000,000")
    .max(20000000, "Please enter amount between $1 to $20,000,000")
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        const numericValue = parseFloat(originalValue.replace(/,/g, ""));
        return isNaN(numericValue) ? undefined : numericValue;
      }
      return value;
    }),
});

export const HasATOSchema = Yup.object().shape({
  hasATOClaims: Yup.boolean()
    .required("ATO is required")
    .oneOf([true, false], "ATO is required"),
});

export const HasATOTaxIncentiveSchema = Yup.object().shape({
  hasTaxIncentive: Yup.boolean()
    .required("ATO tax incentive is required")
    .oneOf([true, false], "ATO tax incentive is required"),
});

export const TaxYearSchema = Yup.object().shape({
  taxYear: Yup.number().required("Tax year is required"),
});

export const EstimatedFilingDateSchema = Yup.object().shape({
  estimatedTaxFilingDate: Yup.string().required(
    "Estimated filing date is required",
  ),
});

export const ExpectedLoanDateSchema = Yup.object().shape({
  expectedLoanDate: Yup.string().required("Expected loan date is required"),
});

export const EstimatedExpenditureSchema = Yup.object().shape({
  estimatedExpenditureAmount: Yup.number()
    .typeError("Minimum amount is 1")
    .required("Estimated expenditure is required")
    .min(1, "Minimum amount is 1")
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        const numericValue = parseFloat(originalValue.replace(/,/g, ""));
        return isNaN(numericValue) ? undefined : numericValue;
      }
      return value;
    }),
});

export const CompanyAddressSchema = Yup.object().shape({
  companyAddress: Yup.string().required("Company address is required"),
});

export const BusinessOrCompanyAddressSchema = Yup.object().shape({
  businnessOrCompanyAddress: Yup.string().required(
    "Business or company address is required",
  ),
});

export const AUSIndustrySchema = Yup.object().shape({
  auRegDate: Yup.date()
    .typeError("Estimated tax filing date must be a valid date")
    .required("AUSIndustry Registration Date is required"),
});

export const YourLoanRNDReviewSchema = Yup.object().shape({
  loanAmount: Yup.number()
    .typeError("Please enter amount between $300,000 to $10,000,000")
    .required("Loan amount is required")
    .min(300000, "Please enter amount between $300,000 to $10,000,000")
    .max(10000000, "Please enter amount between $300,000 to $10,000,000")
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        const numericValue = parseFloat(originalValue.replace(/,/g, ""));
        return isNaN(numericValue) ? undefined : numericValue;
      }
      return value;
    }),
  annualTurnover: Yup.number()
    .typeError("Please enter amount between $1 to $20,000,000")
    .required("Annual Turnover is required")
    .min(1, "Please enter amount between $1 to $20,000,000")
    .max(20000000, "Please enter amount between $1 to $20,000,000")
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        const numericValue = parseFloat(originalValue.replace(/,/g, ""));
        return isNaN(numericValue) ? undefined : numericValue;
      }
      return value;
    }),
  hasATOClaims: Yup.boolean()
    .required("ATO is required")
    .oneOf([true, false], "ATO is required"),
  hasTaxIncentive: Yup.boolean()
    .required("ATO tax incentive is required")
    .oneOf([true, false], "ATO tax incentive is required"),
  taxYear: Yup.number().required("Tax year is required"),
  estimatedTaxFilingDate: Yup.string().required(
    "Estimated filing date is required",
  ),
  estimatedExpenditureAmount: Yup.number()
    .typeError("Minimum amount is 1")
    .required("Estimated expenditure is required")
    .min(1, "Minimum amount is 1")
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        const numericValue = parseFloat(originalValue.replace(/,/g, ""));
        return isNaN(numericValue) ? undefined : numericValue;
      }
      return value;
    }),
});

export const AccountantSchema = Yup.object().shape({
  accountantFirstName: Yup.string()
    .matches(
      /^[A-Za-z]+(?:\s[A-Za-z]+)*$/,
      "First name can only contain alphabet with spaces between words",
    )
    .required("First name is required"),

  accountantLastName: Yup.string()
    .matches(
      /^[A-Za-z]+(?:\s[A-Za-z]+)*$/,
      "Last name can only contain alphabet with spaces between words",
    )
    .required("Last name is required"),

  accountantBusinessName: Yup.string().required("Business name is required"),
  accountantWorkEmailAddress: Yup.string()
    .email("Enter email address in format: yourname@example.com")
    .max(256, "Email can have a maximum of 256 characters")
    .required("Work email address is required")
    .test("is-valid-email", "Invalid Email", (value) => {
      if (!value) return false;
      const domain = value.split("@")[1];

      return true;
    }),
});

// BROKERS
export const brokerLoginValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Enter your email address in format: yourname@example.com")
    .max(256, "Email can have a maximum of 256 characters")
    .required("Email is required")
    .test(
      "is-not-temp-email",
      "Temporary email addresses are not allowed",
      (value) => {
        if (!value) return false;

        if (process.env.REACT_APP_ENV !== "production") return true;

        const domain = value.includes("@")
          ? value.split("@")[1].toLowerCase()
          : null;
        if (!domain) return true;
        return !tempEmailDomains.some(
          (tempDomain) =>
            domain === tempDomain || domain.endsWith(`.${tempDomain}`),
        );
      },
    ),
  password: Yup.string().required("Password is required"),
});

export const changePasswordValidationSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required("New Password is required")
    .min(7, "New Password must have a minimum of 7 characters")
    .max(30, "New Password can have a maximum of 30 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[~`!@#$%^&*()_\-+={\[}\]|\\:;"'<,>.?/])/,
      "Password must contain uppercase, lowercase, number, and special character",
    ),

  confirmNewPassword: Yup.string()
    .required("Confirm new password is required")
    .oneOf([Yup.ref("newPassword")], "Passwords do not match"),
});

export const rndLoanCalculatorSchema = Yup.object().shape({
  estimatedTaxFilingDate: Yup.string().required(
    "Estimated filing date is required",
  ),
  estimatedExpenditureAmount: Yup.number()
    .typeError("Minimum amount is 1")
    .required("Estimated expenditure is required")
    .min(1, "Minimum amount is 1")
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        const numericValue = parseFloat(originalValue.replace(/,/g, ""));
        return isNaN(numericValue) ? undefined : numericValue;
      }
      return value;
    }),
});
